import { reactivePick } from '@vueuse/core';
import { isEqual, diff } from 'ohash/utils';
import type { LinkProps } from 'types/link';

/**
 * Retrieves the value at the specified path of an object. If the resolved value is
 * undefined or null, returns the provided defaultValue.
 *
 * @template T - The expected return type.
 * @param object - The object to query.
 * @param path - The path of the property to get (dot-separated string or array of keys).
 * @param defaultValue - The value returned if the resolved value is undefined.
 * @returns The value at the given path or the defaultValue.
 */
export function get<T = any>(
  object: Record<string, any> | undefined,
  path: (string | number)[] | string,
  defaultValue?: T
): T {
  if (typeof path === 'string') {
    path = path.split('.').map(key => {
      const numKey = Number(key);
      return Number.isNaN(numKey) ? key : numKey;
    });
  }

  let result: any = object;

  for (const key of path) {
    if (result === undefined || result === null) {
      return defaultValue as T;
    }

    result = result[key];
  }

  return result !== undefined ? (result as T) : (defaultValue as T);
}

/**
 * Type guard that checks whether an array contains arrays as its first element.
 *
 * @template A - The type of elements in the array.
 * @param item - The array to check.
 * @returns True if the item is an array of arrays, false otherwise.
 */
export function isArrayOfArray<A>(item: A[] | A[][]): item is A[][] {
  return Array.isArray(item[0]);
}

/**
 * Compares two values for equality. Uses a comparator function, a property key,
 * or deep equality check as fallback.
 *
 * @template T - The type of the values being compared.
 * @param value - The first value to compare.
 * @param currentValue - The second value to compare.
 * @param comparator - Optional. A function or property path string to customize comparison.
 * @returns True if values are considered equal, false otherwise.
 */
export function compare<T>(
  value?: T,
  currentValue?: T,
  comparator?: string | ((a: T, b: T) => boolean)
): boolean {
  if (value === undefined || currentValue === undefined) {
    return false;
  }

  if (typeof value === 'string') {
    return value === currentValue;
  }

  if (typeof comparator === 'function') {
    return comparator(value, currentValue);
  }

  if (typeof comparator === 'string') {
    return get(value!, comparator) === get(currentValue!, comparator);
  }

  return isEqual(value, currentValue);
}

/**
 * Attempts to coerce a value to a number. If parsing fails, returns the original value.
 *
 * @param val - The value to convert.
 * @returns The parsed number or the original value if conversion is not possible.
 */
export function looseToNumber(val: any): any {
  const n = Number.parseFloat(val);
  return Number.isNaN(n) ? val : n;
}

export function pickLinkProps(link: LinkProps & { [key: string]: any }) {
  const keys = Object.keys(link);

  const ariaKeys = keys.filter(key => key.startsWith('aria-'));
  const dataKeys = keys.filter(key => key.startsWith('data-'));

  const propsToInclude = [
    'active',
    'activeClass',
    'ariaCurrentValue',
    'as',
    'disabled',
    'exact',
    'exactActiveClass',
    'exactHash',
    'exactQuery',
    'external',
    'href',
    'download',
    'inactiveClass',
    'noRel',
    'rel',
    'replace',
    'target',
    'to',
    'type',
    'title',
    'onClick',
    ...ariaKeys,
    ...dataKeys
  ];

  const picked = reactivePick(link, ...propsToInclude);

  // Prioritize 'to' over 'href' - if both exist, use 'to' and remove 'href'
  if (picked.to && picked.href) {
    delete picked.href;
  }
  // If only href exists, convert it to 'to'
  else if (picked.href && !picked.to) {
    picked.to = picked.href;
    delete picked.href;
  }

  return picked;
}

export function isPartiallyEqual(item1: any, item2: any) {
  const diffedKeys = diff(item1, item2).reduce((filtered, q) => {
    if (q.type === 'added') {
      filtered.add(q.key);
    }
    return filtered;
  }, new Set<string>());

  const item1Filtered = Object.fromEntries(
    Object.entries(item1).filter(([key]) => !diffedKeys.has(key))
  );
  const item2Filtered = Object.fromEntries(
    Object.entries(item2).filter(([key]) => !diffedKeys.has(key))
  );

  return isEqual(item1Filtered, item2Filtered);
}
